import { bffCrmPersonTimeline } from '@fireflyhealth/api'
import type { BffCrmPersonTimelineRetrieveResponse } from '@fireflyhealth/api'

import { useInfiniteQuery, useQuery } from '~/shared/hooks/query'

/** The server's default page. */
const PAGE_SIZE = 200
/** A single normalized journey event, as the BFF composes it. */
export type TimelineEvent = BffCrmPersonTimelineRetrieveResponse['events'][number]
export type TimelineLane = TimelineEvent['lane']
type TimelinePage = BffCrmPersonTimelineRetrieveResponse
/** The summary card's whole state: the newest usable text, and any run still going. */
export type JourneySummary = NonNullable<BffCrmPersonTimelineRetrieveResponse['summary']>

// Carries `lanes` because the filter is applied server-side: each subset is its own response.
const personTimelineQueryKey = (personId: number, lanes: string | undefined) =>
  bffCrmPersonTimeline.retrieveQueryKey({ person_id: personId }, { lanes })

/** A member's whole journey feed, unpaged; `lanes` is a comma-separated subset, or every lane. */
export const useGetPersonTimeline = (variables: {
  personId: number | undefined
  lanes?: string
}) => {
  const { personId, lanes } = variables
  return useQuery<BffCrmPersonTimelineRetrieveResponse>(
    personId != null
      ? personTimelineQueryKey(personId, lanes)
      : ['retrieveBffCrmPersonTimeline', {}],
    async () =>
      (await bffCrmPersonTimeline.retrieve({ person_id: Number(personId) }, { params: { lanes } }))
        .data,
    { enabled: personId != null },
    { error: 'Failed to fetch member timeline' },
  )
}

/** A member's journey feed a page at a time, newest first. */
export const useGetPersonTimelinePages = (variables: {
  personId: number | undefined
  lanes?: string
}) => {
  const { personId, lanes } = variables
  return useInfiniteQuery(
    personId != null
      ? [...personTimelineQueryKey(personId, lanes), 'pages']
      : ['retrieveBffCrmPersonTimeline', 'pages'],
    async ({ pageParam }: { pageParam: string | undefined }): Promise<TimelinePage> =>
      (
        await bffCrmPersonTimeline.retrieve(
          { person_id: Number(personId) },
          { params: { lanes, page_size: PAGE_SIZE, cursor: pageParam } },
        )
      ).data,
    {
      enabled: personId != null,
      initialPageParam: undefined,
      getNextPageParam: lastPage => lastPage.page?.next ?? undefined,
    },
    { error: 'Failed to fetch member timeline' },
  )
}

/** Events across the pages; a `page: null` response is the whole feed and replaces them. */
export const eventsFromPages = (pages: TimelinePage[]): TimelineEvent[] => {
  const wholeFeed = pages.findLast(page => page.page == null)
  if (wholeFeed != null) return wholeFeed.events
  return pages.flatMap(page => page.events)
}
